import { api } from './client';
import { publicApi } from './publicClient';

/**
 * Xamplin domain data layer (build-v2 F2). The forked core talks to xk-* (Xotski);
 * Xamplin business data lives under xp-*. These are the typed wrappers the
 * Home command center + planner consume.
 */

export interface XpProject {
  id: number;
  companyId: number | null;
  name: string;
  samples: number | null;
  location: string | null;
  startDate: string | null;
  endDate: string | null;
  objectiveType: string | null;
  marketingObjectivePrimary: string | null;
  marketingObjectiveSecondary: string | null;
  image: string | null;
  status: string;
}

/** Asset Purpose nomenclator (SEE/ENGAGE/TRY/ACT) — build-v2 M1. */
export interface XpAssetPurpose {
  id: number;
  name: 'SEE' | 'ENGAGE' | 'TRY' | 'ACT' | string;
  position: number;
}

/** Event asset (build-v2 F6). `assetObjetive` is the purpose id (1-4 = SEE/
 * ENGAGE/TRY/ACT). `description` is the legacy name/filename; `image` the URL. */
export interface XpEventAsset {
  id: number;
  companyId: number | null;
  event: number | null;
  assetObjetive: number | null;
  expectedQuantity: number | null;
  description: string | null;
  image: string | null;
}

/** Measure (build-v2 F8) — MENT + funnel + demographics from CS-1. */
export interface XpMeasure {
  responses: number;
  funnel: { scanned: number; started: number; tried: number; enjoyed: number; shifted: number };
  avgRating: number;
  optIn: number;
  ment: number;
  demographics: {
    gender: { label: string; n: number }[];
    age: { label: string; n: number }[];
  };
}
/** Quasi-DiD lift + stakeholder validation (build-v2 F8, project-level only). */
export interface XpDiD {
  treatedRate: number;
  controlRate: number;
  liftPp: number;
  treatedN: number;
  controlN: number;
}
export interface XpStakeholderValidation {
  total: number;
  aligned: number;
  alignedPct: number;
  avgRating: number;
}
export interface XpProjectMeasure extends XpMeasure {
  projectId: number;
  did?: XpDiD;
  stakeholder?: XpStakeholderValidation;
}
export interface XpEventMeasure extends XpMeasure {
  eventId: number;
  name: string;
  attendance: number;
  stakeholder?: XpStakeholderValidation;
}

/** Purpose id → tier name (matches the seeded xp_event_asset_purpose ids). */
export const PURPOSE_BY_ID: Record<number, 'SEE' | 'ENGAGE' | 'TRY' | 'ACT'> = {
  1: 'SEE',
  2: 'ENGAGE',
  3: 'TRY',
  4: 'ACT',
};

/** BrandKit resolver response — build-v2 M7 (`GET /xp-brandkit`). */
export interface XpBrandKit {
  companyId: number | null;
  brandId: number | null;
  projectId: number | null;
  name: string | null;
  logo: string | null;
  primaryColor: string | null;
  backgroundColor: string | null;
  fontColor: string | null;
  secondaryColor: string | null;
  productVariants: string[];
  competitiveSet: string[];
  surveyConfig?: Record<string, boolean> | null;
}

/** Photo social feed item (build-v2 F9) — `GET /xp-photo/feed`. */
export type PhotoTag = 'add-to-recap' | 'great-work' | 'needs-attention';
export type PhotoReaction = 'fistbump' | 'heart' | 'fire';
export interface XpPhoto {
  id: number;
  image: string;
  description: string | null;
  tags: PhotoTag[];
  approved: boolean;
  purposeId: number | null;
  createdAt: string;
  event: { id: number; name: string } | null;
  group: { id: number; name: string } | null;
  reactions: number;
  comments: number;
}
export interface XpPhotoComment {
  id: number;
  userId: number | null;
  authorName: string | null;
  body: string;
  createdAt?: string;
}
export interface XpPhotoReactionState {
  reaction: PhotoReaction;
  n: number;
  mine: boolean;
}

/** Operations · warehouse (build-v2 G13). */
export interface XpWarehouse {
  id: number;
  name: string;
  location: string | null;
  capacity: number | null;
  notes: string | null;
  companyId?: number;
}

/** Collaborative intake — passwordless scoped invite (build-v2 F7). */
export type InviteScope = 'brand' | 'event' | 'reporter';
export interface XpInvite {
  id: number;
  token: string;
  scope: InviteScope;
  targetId: number | null;
  email: string | null;
  name: string | null;
  role: string | null;
  expiresAt: string | null;
  acceptedAt: string | null;
  usedAt: string | null;
  createdAt: string;
}
export interface XpInviteMint {
  token: string;
  scope: InviteScope;
  targetId: number;
  path: string;
  expiresInHours: number;
}
export interface XpInviteContext {
  scope: InviteScope;
  targetId: number | null;
  email: string | null;
  name: string | null;
  role: string | null;
  expiresAt: string | null;
  termsRequired: boolean;
  context: Record<string, unknown> | null;
}

/** Planner structure (build-v2 F4) as returned by `GET /xp-project/:id/structure`
 * → `{ structured: { project: { eventGroups: [...] } } }`. Node ids are strings
 * like "eventGroup-48" / "event-12" / "station-5"; `entityIdOf()` extracts the
 * numeric id. */
export interface XpStructureNode {
  id: string;
  type?: string;
  name?: string;
  startDate?: string | null;
  endDate?: string | null;
  objective?: string | null;
}
export interface XpStructureStation extends XpStructureNode {
  purpose?: string | null;
  isAnchor?: boolean;
  samples?: number | null;
}
export interface XpStructureReporter extends XpStructureNode {
  email?: string | null;
  reporterType?: string | null;
  surveyType?: string | null;
}
export interface XpStructureEvent extends XpStructureNode {
  address?: string | null;
  location?: string | null;
  attendance?: number;
  budget?: number;
  samples?: number;
  lat?: number | null;
  lng?: number | null;
  stations?: XpStructureStation[];
  reporters?: XpStructureReporter[];
  assets?: unknown[];
}
export interface XpStructureGroup extends XpStructureNode {
  events?: XpStructureEvent[];
}
export interface XpStructureDemographic extends XpStructureNode {
  expectedAudience?: number;
  ageGroup?: string;
  gender?: string;
  ethnicity?: string;
}
export interface XpProjectStructure {
  structured?: {
    project?: {
      id?: string;
      name?: string;
      eventGroups?: XpStructureGroup[];
      demographics?: XpStructureDemographic[];
    };
  };
  mappings?: unknown[];
}

/** "eventGroup-48" → 48. Falls back to NaN-safe 0. */
export function entityIdOf(nodeId: string | undefined): number {
  if (!nodeId) return 0;
  const n = Number(nodeId.split('-').pop());
  return Number.isFinite(n) ? n : 0;
}

/** Derive event lifecycle status from its dates — the backend has no status
 * column yet (build-v2: status derivation). Shared by the planner + Home PTM. */
export function deriveEventStatus(ev: {
  startDate?: string | null;
  endDate?: string | null;
}): 'plan' | 'live' | 'completed' {
  const now = Date.now();
  const start = ev.startDate ? Date.parse(ev.startDate) : NaN;
  const end = ev.endDate ? Date.parse(ev.endDate) : NaN;
  if (Number.isFinite(end) && end < now) return 'completed';
  if (Number.isFinite(start) && Number.isFinite(end) && start <= now && now <= end)
    return 'live';
  return 'plan';
}

export const xamplinApi = {
  projects: {
    list: (companyId?: number) =>
      api.list<XpProject>(
        '/xp-project',
        companyId ? { filter: { companyId }, perPage: 200 } : { perPage: 200 },
      ),
    structure: (id: number) => api.get<XpProjectStructure>(`/xp-project/${id}/structure`),
  },

  eventGroups: {
    create: (data: { name: string; project: number; companyId: number }) =>
      api.post('/xp-event-group', data),
  },
  events: {
    create: (data: {
      name: string;
      eventGroupId: number;
      companyId: number;
      attendance?: number;
    }) => api.post('/xp-events', { attendance: 0, ...data }),
  },
  stations: {
    create: (data: {
      name: string;
      eventId: number;
      companyId: number;
      isAnchor?: boolean;
    }) => api.post('/xp-station', data),
  },
  reporters: {
    // build-v2 F5 — assign a reporter to an event by email (finds/creates the
    // user, enforces the ≤4/event cap). `event` is the numeric event id.
    assign: (data: {
      event: number;
      email: string;
      firstname: string;
      lastname: string;
      surveyType?: string;
    }) => api.post('/xp-event-has-reporter/assign', { surveyType: 'RS-1', ...data }),
  },

  assetPurposes: () => api.list<XpAssetPurpose>('/xp-event-asset-purpose', { perPage: 10 }),

  assets: {
    list: (perPage = 300) => api.list<XpEventAsset>('/xp-event-has-asset', { perPage }),
    /** Set the Asset Purpose (build-v2 G11 — classify / bulk-classify). */
    classify: (id: number, assetObjetive: number) =>
      api.put<XpEventAsset>(`/xp-event-has-asset/${id}`, { assetObjetive }),
  },

  /** MENT + trial-validation funnel + demographics for a project (build-v2 F8). */
  projectMeasure: (id: number) => api.get<XpProjectMeasure>(`/xp-analytics/project/${id}/measure`),
  eventMeasure: (id: number) => api.get<XpEventMeasure>(`/xp-analytics/event/${id}/measure`),

  /** Operations · warehouses (build-v2 G13). */
  warehouses: {
    list: () => api.get<XpWarehouse[]>('/xp-warehouse'),
    create: (data: { name: string; location?: string; capacity?: number; notes?: string }) =>
      api.post<XpWarehouse>('/xp-warehouse', data),
    remove: (id: number) => api.delete(`/xp-warehouse/${id}`),
  },

  /** Photo social layer (build-v2 F9). */
  photos: {
    feed: (params: { eventId?: number; groupId?: number; tag?: PhotoTag; approved?: boolean; limit?: number } = {}) => {
      const q = new URLSearchParams();
      if (params.eventId) q.set('eventId', String(params.eventId));
      if (params.groupId) q.set('groupId', String(params.groupId));
      if (params.tag) q.set('tag', params.tag);
      if (params.approved) q.set('approved', 'true');
      if (params.limit) q.set('limit', String(params.limit));
      const qs = q.toString();
      return api.get<{ items: XpPhoto[]; total: number }>(`/xp-photo/feed${qs ? `?${qs}` : ''}`);
    },
    reactions: (id: number) => api.get<XpPhotoReactionState[]>(`/xp-photo/${id}/reactions`),
    react: (id: number, reaction: PhotoReaction) =>
      api.post<{ reaction: PhotoReaction; mine: boolean }>(`/xp-photo/${id}/react`, { reaction }),
    comments: (id: number) => api.get<XpPhotoComment[]>(`/xp-photo/${id}/comments`),
    comment: (id: number, body: string) => api.post<XpPhotoComment>(`/xp-photo/${id}/comment`, { body }),
    toggleTag: (id: number, tag: PhotoTag) =>
      api.post<{ id: number; tags: PhotoTag[] }>(`/xp-photo/${id}/tag`, { tag }),
    setApproval: (id: number, approved: boolean) =>
      api.post<{ id: number; approved: boolean }>(`/xp-photo/${id}/approval`, { approved }),
  },

  /** Collaborative intake — passwordless scoped invites (build-v2 F7). */
  intake: {
    create: (data: {
      scope: InviteScope;
      targetId: number;
      email?: string;
      name?: string;
      role?: string;
      ttlHours?: number;
    }) => api.post<XpInviteMint>('/xp-invite', data),
    list: (scope?: InviteScope, targetId?: number) => {
      const q = new URLSearchParams();
      if (scope) q.set('scope', scope);
      if (targetId) q.set('targetId', String(targetId));
      const qs = q.toString();
      return api.get<XpInvite[]>(`/xp-invite${qs ? `?${qs}` : ''}`);
    },
    revoke: (id: number) => api.delete(`/xp-invite/${id}`),
    // Public (token-authed) — the contributor never logs in.
    verify: (token: string) => publicApi.post<XpInviteContext>('/xp-invite/verify', { token }),
    submit: (token: string, data: Record<string, unknown>) =>
      publicApi.post<{ ok: boolean; scope: InviteScope }>('/xp-invite/submit', { token, data }),
  },

  brandKit: (params: { companyId?: number; brandId?: number; projectId?: number }) => {
    const q = new URLSearchParams();
    if (params.companyId) q.set('companyId', String(params.companyId));
    if (params.brandId) q.set('brandId', String(params.brandId));
    if (params.projectId) q.set('projectId', String(params.projectId));
    return api.get<XpBrandKit>(`/xp-brandkit?${q.toString()}`);
  },
  /** Persist Brand Studio identity edits (build-v2 F3). */
  brandKitUpdate: (
    companyId: number,
    dto: {
      logo?: string | null;
      backgroundColor?: string | null;
      fontColor?: string | null;
      secondaryColor?: string | null;
      productVariants?: string[];
      competitiveSet?: string[];
      surveyConfig?: Record<string, boolean>;
    },
  ) => api.put<XpBrandKit>(`/xp-brandkit/${companyId}`, dto),
};
